import Model from '@ember-data/model';
import { computed } from '@ember/object';
import { notEmpty } from '@ember/object/computed';
import attr from 'ember-data/attr';
import { IntegrationIDs, IntegrationKeys } from 'cio/utils/integrations/static-integrations';
import { isNone } from '@ember/utils';
import { WarehouseDestinationID, WarehouseStorageConfig } from 'cio/utils/warehouse';

const LATEST_SCHEMA_VERSION = 4;

const INTEGRATION_CONFIG_MAP: Record<IntegrationKeys, keyof WarehouseSyncModel> = {
  [IntegrationIDs.azureBlob]: 'azureConfig',
  [IntegrationIDs.gcs]: 'gcsConfig',
  s3: 's3Config',
  [IntegrationIDs.yandex]: 'yandexConfig',
};

export interface WarehouseSyncS3Config {
  bucketName: string;
  bucketPath: string;
  secretKey: string;
  accessKey: string;
  region: string;
}

export interface WarehouseSyncGCSConfig {
  bucketName: string;
  bucketPath: string;
  credentials: string;
}

export interface WarehouseSyncAzureBlobConfig {
  bucketPath: string;
  containerSasUrl: string;
}

export interface WarehouseSyncYandexConfig {
  bucketName: string;
  bucketPath: string;
  secretKey: string;
  accessKey: string;
  region: string;
}

export interface WarehouseSyncExportFile {
  id: string;
  fileName: string;
  createdAt: number;
}

export function createConfig(
  destination: WarehouseDestinationID,
): WarehouseSyncS3Config | WarehouseSyncGCSConfig | WarehouseSyncAzureBlobConfig | WarehouseSyncYandexConfig | null {
  if (destination === WarehouseDestinationID.s3) {
    return {
      bucketName: '',
      bucketPath: '',
      secretKey: '',
      accessKey: '',
      region: '',
    } as WarehouseSyncS3Config;
  }

  if (destination === WarehouseDestinationID.gcs) {
    return {
      bucketName: '',
      bucketPath: '',
      credentials: '',
    } as WarehouseSyncGCSConfig;
  }

  if (destination === WarehouseDestinationID.azure) {
    return { bucketPath: '', containerSasUrl: '' } as WarehouseSyncAzureBlobConfig;
  }

  if (destination === WarehouseDestinationID.yandex) {
    return {
      bucketName: '',
      bucketPath: '',
      secretKey: '',
      accessKey: '',
      region: '',
    } as WarehouseSyncYandexConfig;
  }

  return null;
}

export default class WarehouseSyncModel extends Model {
  @attr('camel-case') s3Config!: WarehouseSyncS3Config;
  @attr('camel-case') gcsConfig!: WarehouseSyncGCSConfig;
  @attr('camel-case') azureConfig!: WarehouseSyncAzureBlobConfig;
  @attr('camel-case') yandexConfig!: WarehouseSyncYandexConfig;

  @attr('camel-case', { defaultValue: () => [] }) exportFiles!: WarehouseSyncExportFile[];

  @attr('number') version!: number;
  @attr('boolean', { defaultValue: true }) deliveries!: boolean;
  @attr('boolean', { defaultValue: true }) contents!: boolean;
  @attr('boolean', { defaultValue: true }) metrics!: boolean;
  @attr('boolean', { defaultValue: true }) subjects!: boolean;
  @attr('boolean', { defaultValue: true }) outputs!: boolean;
  @attr('boolean', { defaultValue: true }) people!: boolean;
  @attr('boolean', { defaultValue: true }) attributes!: boolean;

  @attr('string') error!: string;

  @attr('number') peopleLastUpdatedAt!: number;
  @attr('number') createdAt!: number;

  @attr('string') updateType!: 'settings' | 's3_config' | 'gcs_config' | 'azure_config';

  @notEmpty('error') hasError!: boolean;

  @computed('s3Config', 'gcsConfig', 'azureConfig')
  get type() {
    return this.getCurrentlyConfiguredStorageIntegration();
  }

  private getCurrentlyConfiguredStorageIntegration() {
    let isIntegrationConfigured = (key: string) => !isNone(this.get(INTEGRATION_CONFIG_MAP[key]));
    return Object.keys(INTEGRATION_CONFIG_MAP).find(isIntegrationConfigured) ?? '';
  }

  @computed('deliveries', 'contents', 'metrics', 'subjects', 'outputs', 'people', 'attributes')
  get isPaused(): boolean {
    return ['deliveries', 'contents', 'metrics', 'subjects', 'outputs', 'people', 'attributes'].every(
      (key: keyof WarehouseSyncModel) => this[key] === false,
    );
  }

  @computed('type')
  get bucketName() {
    let config = this.get(INTEGRATION_CONFIG_MAP[this.type]) as WarehouseStorageConfig;
    if ('bucketName' in config) {
      return config.bucketName;
    } else if ('bucketPath' in config) {
      return config.bucketPath;
    }

    return '';
  }

  @computed('version')
  get canUpdateSchema() {
    return this.version < LATEST_SCHEMA_VERSION;
  }
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'warehouse-sync': WarehouseSyncModel;
  }
}
